/**
 * Which banner wins when two conditions want the screen at once.
 *
 * There is one banner slot in `Rehearsal.tsx`, and several independent
 * detectors can want it in the same second: the socket reports the demo is
 * out of credits while the mic monitor decides the input is silent. Without
 * a stated rule, last-writer-wins, and the last writer is whichever timer
 * happened to fire second.
 *
 * The rule is severity rank, highest wins, ties go to the newer banner:
 *
 *   100  cost caps        The session is over. No mic fix helps, so nothing
 *                         may overwrite this — it is the one state where the
 *                         visitor's next action matters (come back tomorrow)
 *                         and a mic hint would send them the wrong way.
 *    80  mic unusable     Voice cannot start at all. Outranks anything about
 *                         voice running badly, because there is no session
 *                         yet to run badly.
 *    60  input degraded   Voice started and is producing nothing useful.
 *    40  output degraded  Input works; the reply just will not speak. The
 *                         visitor can still see the transcript, so this is
 *                         the least of the failures.
 *    20  paused           Not a failure at all.
 *
 * Ties resolve to the newer banner because two conditions of equal severity
 * are equally worth saying, and the fresher one describes the state the
 * visitor is in now.
 */

import type { FallbackBanner, VoiceFallbackKind, VoiceStoreSlice } from '@/voice/types';

const RANK: Record<VoiceFallbackKind, number> = {
  'cost-cap-anon': 100,
  'cost-cap-402': 100,
  'demo-at-capacity': 100,
  'no-get-user-media': 80,
  'mic-permission-denied': 80,
  'mic-unavailable': 80,
  'silent-stream': 60,
  'stt-ws-fail': 60,
  'tts-fail': 40,
  'tab-background': 20,
};

/**
 * Banner kinds owned by the mic layer. Only these may be cleared when a mic
 * condition resolves — clearing by "is a banner showing?" would wipe a cap
 * banner the moment the visitor's mic permission flipped to granted.
 */
export const MIC_OWNED_KINDS: readonly VoiceFallbackKind[] = [
  'no-get-user-media',
  'mic-permission-denied',
  'mic-unavailable',
  'silent-stream',
];

/** True when `kind` may take the screen from whatever is showing now. */
export function canDisplace(
  current: FallbackBanner | null,
  kind: VoiceFallbackKind,
): boolean {
  if (current === null) return true;
  return RANK[kind] >= RANK[current.kind];
}

/**
 * Show `banner` unless something more severe already holds the slot.
 * Returns whether it was shown, so callers can avoid side effects (mode
 * changes, stream teardown) that would contradict a banner that outranks them.
 */
export function showBanner(store: VoiceStoreSlice, banner: FallbackBanner): boolean {
  if (!canDisplace(store.activeBanner, banner.kind)) return false;
  store.setActiveBanner(banner);
  return true;
}

/** Clear the banner only if it is one of `kinds`. */
export function clearBannerKinds(
  store: VoiceStoreSlice,
  kinds: readonly VoiceFallbackKind[],
): void {
  const current = store.activeBanner;
  if (current !== null && kinds.includes(current.kind)) {
    store.setActiveBanner(null);
  }
}
